import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BookmarkButton({ 
  businessId, 
  variant = 'default', // 'default', 'icon', 'card'
  className = '',
  showCount = false,
  onToggle = null 
}) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (token && businessId) {
      checkBookmark();
    } else {
      setChecking(false);
    }
  }, [businessId, token]);

  const checkBookmark = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/bookmarks/check/${businessId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const { bookmarked } = await res.json();
        setIsBookmarked(bookmarked);
      }
    } catch (error) {
      console.error('Failed to check bookmark:', error);
    } finally {
      setChecking(false);
    }
  };

  const toggleBookmark = async () => {
    if (!token) {
      toast.error('Please log in to bookmark businesses');
      return;
    }

    setLoading(true);
    try {
      if (isBookmarked) {
        // Remove bookmark
        const res = await fetch(`${API_URL}/api/social/bookmarks/${businessId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setIsBookmarked(false);
          toast.success('Bookmark removed');
          onToggle && onToggle(false);
        }
      } else {
        // Add bookmark
        const res = await fetch(`${API_URL}/api/social/bookmarks`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ business_id: businessId })
        });
        if (res.ok) {
          setIsBookmarked(true);
          toast.success('Business bookmarked!');
          onToggle && onToggle(true);
        }
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Button variant="ghost" size="icon" disabled className={className}>
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  // Icon only variant
  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleBookmark}
        disabled={loading}
        className={`relative ${isBookmarked ? 'text-amber-400' : 'text-gray-400 hover:text-amber-400'} ${className}`}
        data-testid={`bookmark-btn-${businessId}`}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <motion.div
            initial={false}
            animate={{ scale: isBookmarked ? [1, 1.3, 1] : 1 }}
            transition={{ duration: 0.3 }}
          >
            <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-amber-400' : ''}`} />
          </motion.div>
        )}
      </Button>
    );
  }

  // Card variant (for business cards)
  if (variant === 'card') {
    return (
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleBookmark();
        }}
        disabled={loading}
        className={`p-2 rounded-full hover:bg-white/10 transition-colors ${
          isBookmarked ? 'text-amber-400' : 'text-gray-400 hover:text-amber-400'
        } ${className}`}
        data-testid={`bookmark-card-btn-${businessId}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-amber-400' : ''}`} />
        )}
      </button>
    );
  }

  // Default button variant
  return (
    <Button
      variant={isBookmarked ? 'default' : 'outline'}
      onClick={toggleBookmark}
      disabled={loading}
      className={`${
        isBookmarked 
          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
          : 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10'
      } ${className}`}
      data-testid={`bookmark-default-btn-${businessId}`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Bookmark className={`w-4 h-4 mr-2 ${isBookmarked ? 'fill-white' : ''}`} />
      )}
      {isBookmarked ? 'Bookmarked' : 'Bookmark'}
    </Button>
  );
}

export default BookmarkButton;
